import React from 'react';
import { MenuItem, ListItemText } from '@mui/material';

// bookGroups.js — shared "combo" presets for Books dropdowns app-wide.
// Group membership is matched fuzzily against each page's available book names
// (case/punctuation-insensitive substring both ways), so shorthand like
// 'mgm' matches 'BetMGM' and books not present on a page simply drop out.
// Single source of truth: edit the lists here, every dropdown follows.

export const BOOK_GROUPS = [
  {
    label: 'Domestic',
    books: ['draftkings', 'betmgm', 'caesars', 'fanduel', 'betrivers', 'hard rock', 'fanatics', 'thescore', 'unibet', 'bet365', 'circa'],
  },
  {
    label: 'Sweepstakes',
    books: ['rebet', 'courtside', 'onyx', 'fliff'],
  },
  {
    label: 'Prediction Markets',
    books: ['novig', 'prophetx', 'prophet x', 'kalshi', 'polymarket', 'sporttrade'],
  },
  {
    label: 'Offshores',
    books: ['pinnacle', 'bovada', '1xbet', '1x bet', 'bet105', 'betway', 'bookmaker', 'playdoit', 'prop builder'],
  },
  {
    label: "Pick'ems",
    books: ['underdog', 'pick 6', 'pick6', 'dk pick 6', 'dkpick6', 'sleeper', 'prizepicks'],
  },
];

const norm = (s) => String(s).toLowerCase().replace(/[^a-z0-9]/g, '');

// Books from `availableBooks` that belong to `group`.
// Match rule: exact normalized equality, or the book name starts with the
// fragment ('circa' → 'Circa Sports'), or the fragment ends with the book name
// ('betmgm' matches a book literally named 'MGM'). Deliberately NOT loose
// substring — 'theScore Bet' normalizes to a string that *ends* in 'rebet'.
export function booksInGroup(group, availableBooks) {
  const frags = group.books.map(norm);
  return (availableBooks || []).filter((b) => {
    const nb = norm(b);
    if (!nb) return false;
    return frags.some((f) => nb === f || nb.startsWith(f) || f.endsWith(nb));
  });
}

// Sentinel helpers for MUI multi-Select menu items.
export const GROUP_PREFIX = 'group:';
export const isGroupValue = (v) => typeof v === 'string' && v.startsWith(GROUP_PREFIX);
export const groupFromValue = (v) => BOOK_GROUPS.find((g) => GROUP_PREFIX + g.label === v);

// If the last-clicked value is a group sentinel, return the resolved book list
// (books of that group present in `options`); otherwise null. Lets page handlers do:
//   const groupSel = resolveBooksSelection(val, options);
//   if (groupSel) { setter(groupSel); return; }
export function resolveBooksSelection(value, options) {
  const clicked = value[value.length - 1];
  if (!isGroupValue(clicked)) return null;
  const g = groupFromValue(clicked);
  return g ? booksInGroup(g, options) : [];
}

// Preset rows for a Books dropdown. MUST be called as a function inside the
// <Select> children ({bookGroupMenuItems(opts)}) — NOT wrapped in a component,
// because MUI Select only wires value/click handling into direct children.
export function bookGroupMenuItems(availableBooks, fontSize = '0.8rem') {
  return BOOK_GROUPS.map((g) => {
    const matches = booksInGroup(g, availableBooks);
    if (matches.length === 0) return null;
    return (
      <MenuItem key={GROUP_PREFIX + g.label} value={GROUP_PREFIX + g.label} dense>
        <ListItemText
          primary={`${g.label} (${matches.length})`}
          sx={{ '& .MuiTypography-root': { fontSize, fontStyle: 'italic', color: '#ff8a80' } }}
        />
      </MenuItem>
    );
  });
}